import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';
import { useAuth } from '../../../../providers/AuthProvider';
import { formatShippingAddress, shippingAddressesMatch } from './shippingAddressMatch';

interface MergeOrdersModalProps {
  show: boolean;
  onHide: () => void;
  orders: OrderSummary[];
  onMerged: () => void;
}

export function MergeOrdersModal({ show, onHide, orders, onMerged }: MergeOrdersModalProps) {
  const oms = useOMS();
  const { user } = useAuth();
  const [primaryOrderId, setPrimaryOrderId] = useState('');
  const [isMerging, setIsMerging] = useState(false);

  // Default the primary selection to the first order whenever the selection changes.
  useEffect(() => {
    if (orders.length > 0 && !orders.some((order) => order.orderId === primaryOrderId)) {
      setPrimaryOrderId(orders[0]?.orderId ?? '');
    }
  }, [orders, primaryOrderId]);

  const addressesMatch = shippingAddressesMatch(orders);

  const handleMerge = async () => {
    if (!primaryOrderId || !user || orders.length < 2) {
      return;
    }

    setIsMerging(true);
    try {
      const mergeOrderIds = orders.map((order) => order.orderId).filter((orderId) => orderId !== primaryOrderId);
      const response = await oms.mergeOrders({
        primaryOrderId,
        mergeOrderIds,
        auditEntry: { operator: user.name, source: 'OPERATOR' },
      });

      if (response) {
        toast.success(`Orders merged into #${response.orderId} successfully`);
        onHide();
        onMerged();
      }
    } catch (error) {
      console.error('Error merging orders:', error);
      toast.error('Failed to merge orders: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Merge Orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-warning" role="alert">
          <strong>⚠️ Important Warning:</strong>
          <ul className="mb-0 mt-2">
            <li>Line items from the other orders will be merged into the primary order.</li>
            <li>Orders must share the same shipping address and currency, and must not be shipped yet.</li>
          </ul>
        </div>

        <p>Select the primary order. The other selected orders will be merged into it.</p>
        <Form.Group>
          {orders.map((order) => (
            <Form.Check
              key={order.orderId}
              type="radio"
              name="mergePrimaryOrder"
              id={`merge-primary-${order.orderId}`}
              checked={primaryOrderId === order.orderId}
              onChange={() => setPrimaryOrderId(order.orderId)}
              label={
                <span>
                  <strong>#{order.orderId}</strong>{' '}
                  <span className="text-muted small">
                    {formatShippingAddress(order) || '-'} · {order.currency} {order.totalAmount} ·{' '}
                    {order.lineItems.length} items
                  </span>
                </span>
              }
            />
          ))}
        </Form.Group>

        <div className={`mt-3 small ${addressesMatch ? 'text-success' : 'text-danger'}`}>
          {addressesMatch ? (
            <span>✓ Shipping addresses match.</span>
          ) : (
            <span>✗ Shipping addresses do not match. Orders with different shipping addresses cannot be merged.</span>
          )}
        </div>

        <Form.Group className="mt-3">
          <Form.Label>Operator</Form.Label>
          <Form.Control type="text" value={user?.name || ''} disabled />
          <Form.Text className="text-danger">This will be logged for audit purpose.</Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isMerging}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleMerge}
          disabled={!primaryOrderId || orders.length < 2 || !addressesMatch || isMerging}
        >
          {isMerging ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Merging...
            </>
          ) : (
            'Merge Orders'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
